import { Button } from '@/components/ui/button';
import { useContentDetail } from '@/hooks/useContentQueries';
import { OBSERVATION_CAPTURE_LABELS } from '@/components/widget/observation-capture/labels';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AudioPlayer } from '@/features/viewers/Audio';
import { ChevronUp, ExternalLink, Link2, MoreHorizontal, Plus, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { resolveLinkSeed } from './provenance-link-seed';

export interface ProvenanceRecordingListItem {
  id: string;
  name: string;
  remoteId?: number;
}

export interface ProvenanceRecordingListProps {
  recordings: ProvenanceRecordingListItem[];
  draftText?: string;
  onAddUnlinked?: () => void;
  onAddLinked?: (linkContentId?: number) => void;
  onRemove?: (recordingId: string) => void;
  audioUrlsById?: Record<string, string>;
  onOpenContent?: (contentId: number) => void;
}

function RecordingItem({
  recording,
  audioUrl,
  onRemove,
  onOpenContent,
}: {
  recording: ProvenanceRecordingListItem;
  audioUrl?: string;
  onRemove?: (id: string) => void;
  onOpenContent?: (contentId: number) => void;
}) {
  const detailQuery = useContentDetail(recording.remoteId ?? 0);
  const detail = detailQuery.data;
  const remoteId = recording.remoteId;
  const isRemoteRecording = detail?.contentType === 'RECORDING';
  const title =
    typeof detail?.title === 'string' && detail.title.trim().length > 0
      ? detail.title.trim()
      : recording.name;
  const transcript = isRemoteRecording ? detail.transcript : null;
  const description =
    typeof detail?.description === 'string' && detail.description.trim().length > 0
      ? detail.description.trim()
      : null;
  const [activeTab, setActiveTab] = useState<'summary' | 'transcript'>('summary');
  const [expanded, setExpanded] = useState(false);
  const activeText = useMemo(() => {
    if (activeTab === 'summary') {
      return description;
    }
    return transcript;
  }, [activeTab, description, transcript]);
  const placeholderText = activeTab === 'summary' ? 'Summary processing...' : 'Transcript processing...';
  const resolvedAudioUrl =
    audioUrl ||
    (isRemoteRecording && typeof detail?.uri === 'string' && !detail.uri.startsWith('text-')
      ? `/api/files/recording/${detail.uri}`
      : undefined);

  return (
    <div className="rounded border p-2 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium text-xs">{title}</p>
        <div className="flex items-center gap-1">
          {typeof remoteId === 'number' && onOpenContent ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              aria-label="Open recording"
              title="Open recording"
              onClick={() => onOpenContent(remoteId)}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          ) : null}
          {onRemove ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => onRemove(recording.id)}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          ) : null}
        </div>
      </div>

      {resolvedAudioUrl ? <AudioPlayer src={resolvedAudioUrl} /> : null}

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'summary' | 'transcript')}>
        <TabsList className="h-7">
          <TabsTrigger value="summary" className="text-[11px] px-2">
            Summary
          </TabsTrigger>
          <TabsTrigger value="transcript" className="text-[11px] px-2">
            Transcript
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {activeText ? (
        <div className="flex items-start gap-1">
          <p className={`text-xs text-muted-foreground flex-1 ${expanded ? 'whitespace-pre-wrap' : 'truncate'}`}>
            {activeText}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={() => setExpanded((previous) => !previous)}
            title={expanded ? 'Show less' : 'Show more'}
          >
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <MoreHorizontal className="h-3.5 w-3.5" />}
          </Button>
        </div>
      ) : recording.remoteId ? (
        <p className="text-xs text-muted-foreground">{placeholderText}</p>
      ) : null}
    </div>
  );
}

export function ProvenanceRecordingList({
  recordings,
  draftText = '',
  onAddUnlinked,
  onAddLinked,
  onRemove,
  audioUrlsById = {},
  onOpenContent,
}: ProvenanceRecordingListProps) {
  if (!draftText.trim() && recordings.length === 0) return null;

  // This list also renders on draftText alone, so a zero-recording card is reachable —
  // Add Linked needs something to link to and is hidden when there is nothing.
  const linkSeed = resolveLinkSeed(recordings);

  return (
    <div className="py-2 text-xs bg-muted/30 space-y-1">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium">Recordings</p>
        <div className="flex items-center gap-2">
          {onAddUnlinked ? (
            <Button variant="outline" size="sm" onClick={onAddUnlinked}>
              <Plus className="h-3 w-3 mr-1" />
              {OBSERVATION_CAPTURE_LABELS.addUnlinked}
            </Button>
          ) : null}
          {onAddLinked && linkSeed !== undefined ? (
            <Button variant="outline" size="sm" onClick={() => onAddLinked(linkSeed)}>
              <Link2 className="h-3 w-3 mr-1" />
              {OBSERVATION_CAPTURE_LABELS.addLinked}
            </Button>
          ) : null}
        </div>
      </div>
      {recordings.length > 0 ? (
        <div className="space-y-2">
          {recordings.map((recording) => (
            <RecordingItem
              key={recording.id}
              recording={recording}
              audioUrl={audioUrlsById[recording.id]}
              onRemove={onRemove}
              onOpenContent={onOpenContent}
            />
          ))}
        </div>
      ) : null}
      {draftText.trim() ? (
        <p className="text-muted-foreground">{draftText.trim().length} characters of typed notes</p>
      ) : null}
    </div>
  );
}
